'use client'

import Link from 'next/link'
import { useState } from 'react'
import MarketingNav from '@/components/marketing/Nav'
import MarketingFooter from '@/components/marketing/Footer'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.pulsesignal.co'

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; requestId: string; expectedWithin: string }
  | { kind: 'error'; message: string }

export default function ErasureRequestForm() {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [companyRefsRaw, setCompanyRefsRaw] = useState('')
  const [context, setContext] = useState('')
  const [state, setState] = useState<SubmitState>({ kind: 'idle' })

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (state.kind === 'submitting') return
    if (fullName.trim().length < 2) {
      setState({ kind: 'error', message: 'Enter your full legal name (at least 2 characters).' })
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setState({ kind: 'error', message: 'Enter a valid email address for correspondence.' })
      return
    }
    setState({ kind: 'submitting' })

    const company_references = companyRefsRaw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)

    try {
      const resp = await fetch(`${API_URL}/api/v1/public/gdpr/erasure-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: fullName.trim(),
          email: email.trim(),
          company_references: company_references.length ? company_references : undefined,
          additional_context: context.trim() || undefined,
        }),
      })

      if (resp.status === 429) {
        setState({
          kind: 'error',
          message:
            'Too many requests from your network in the last hour. Please wait and try again, or email privacy@pulsesignal.co directly.',
        })
        return
      }

      if (!resp.ok) {
        const detail = await resp.text().catch(() => '')
        setState({
          kind: 'error',
          message: `Request failed (HTTP ${resp.status}). ${detail.slice(0, 240)}`.trim(),
        })
        return
      }

      const data = await resp.json()
      setState({
        kind: 'success',
        requestId: data.request_id,
        expectedWithin: data.expected_response_within,
      })
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Network error. Please try again.',
      })
    }
  }

  return (
    <main className="bg-cream-100 text-ink-900 min-h-screen relative">
      <MarketingNav />

      <article className="editorial relative pt-28 sm:pt-36 pb-20 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Link href="/privacy" className="mono text-caption uppercase tracking-widest text-ink-600 hover:text-terra-500">
            Privacy
          </Link>
          <span className="text-ink-600">/</span>
          <span className="mono text-caption uppercase tracking-widest text-ink-900">
            Erasure request
          </span>
        </div>

        <h1 className="text-h-xl font-semibold tracking-[-0.035em] leading-[0.98] text-ink-900 text-balance">
          Right-to-erasure request
        </h1>

        {/* Two-column on lg+: preamble + reassurance on the left, form on
            the right. Single-column stack below lg. Replaces the previous
            max-w-2xl mx-auto centered layout that left ~50% horizontal
            whitespace on desktop. */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <p className="lede text-ink-700 text-pretty">
              Use this form to ask PulseSignal to remove personal data we hold about you, for
              example a director record we have mirrored from UK Companies House or a leadership
              profile drawn from a company team page.
            </p>
            <p className="mt-5 text-body-sm text-ink-700 leading-relaxed">
              We do not delete on submission; an operator reviews each request, may contact you for verification,
              and applies the deletion together with a tombstone so subsequent ingests cannot
              resurrect the record.
            </p>

            <div className="mt-6 rounded-2xl bg-cream-50 border border-ink-900/10 p-5">
              <p className="mono text-eyebrow uppercase tracking-widest text-ink-600 font-semibold mb-2">
                Which law backs your request
              </p>
              <ul className="text-body-sm text-ink-700 leading-relaxed list-disc ml-5 space-y-1.5">
                <li><span className="font-semibold text-ink-900">EU / UK / Switzerland</span>: GDPR Article 17 and UK GDPR. Response within 30 days (extendable by 60 days for complex cases under Article 12(3)).</li>
                <li><span className="font-semibold text-ink-900">California (CCPA / CPRA)</span>: § 1798.105 right to delete. Response within 45 days (extendable by 45 days with notice).</li>
                <li><span className="font-semibold text-ink-900">Other US states (VA / CO / CT / UT / TX / OR / MT and 10 more)</span>: equivalent right to delete. Same 45-day default.</li>
                <li><span className="font-semibold text-ink-900">Brazil (LGPD)</span>: Article 18(VI) right to deletion. Response within 15 days.</li>
                <li><span className="font-semibold text-ink-900">India (DPDP 2023)</span>: Section 12 right to erasure. Acknowledged within 72 hours; resolved within 30 days. Grievance Officer: privacy@pulsesignal.co.</li>
                <li><span className="font-semibold text-ink-900">Canada (PIPEDA / Quebec Law 25)</span>: Principle 4.5 and Section 28.1 right to deletion. Response within 30 days.</li>
                <li><span className="font-semibold text-ink-900">Singapore (PDPA), Australia (Privacy Act), South Africa (POPIA), South Korea (PIPA)</span>: equivalent erasure / deletion rights. Response within 30 days.</li>
              </ul>
            </div>

            <div className="mt-5 rounded-2xl bg-cream-50 border border-ink-900/10 p-5">
              <p className="mono text-eyebrow uppercase tracking-widest text-ink-600 font-semibold mb-2">
                When we may decline
              </p>
              <p className="text-body-sm text-ink-700 leading-relaxed">
                We may refuse or partly refuse if the record is a mirror of a statutory public registry that
                law requires us to keep, if deletion would interfere with the defence of a legal claim, or if
                the request is manifestly unfounded or excessive. We will tell you in writing what we are
                doing, why, and how to escalate.
              </p>
            </div>

            <div className="mt-5 rounded-2xl bg-cream-50 border border-ink-900/10 p-5">
              <p className="mono text-eyebrow uppercase tracking-widest text-ink-600 font-semibold mb-2">
                Right to appeal / complain
              </p>
              <p className="text-body-sm text-ink-700 leading-relaxed">
                If we decline or partly decline, you can appeal by replying to our response. A second reviewer
                will look at it afresh. You also have an independent right to complain to your regulator, listed at{' '}
                <a className="text-terra-500 hover:text-terra-600 underline underline-offset-2" href="/privacy/regional">/privacy/regional</a>
                {' '}(ICO for the UK; BfDI for Germany; CPPA for California; ANPD for Brazil; OPC for Canada; PDPC for Singapore; OAIC for Australia; and others).
              </p>
            </div>

            <div className="mt-5 rounded-2xl bg-cream-50 border border-ink-900/10 p-5">
              <p className="mono text-eyebrow uppercase tracking-widest text-ink-600 font-semibold mb-2">
                Identity verification
              </p>
              <p className="text-body-sm text-ink-700 leading-relaxed">
                For requests from the email on your PulseSignal account we match the inbound address against
                our records; that is usually enough. For requests about an officer record where you are not
                the account holder, or for requests by an authorised agent, we may ask for one or more of: a
                signed declaration, government-issued ID number plus a redacted copy of the supporting
                document, or a copy of the written authorisation. We will tell you in writing what we need
                and why, and we destroy identity evidence after the request is closed.
              </p>
            </div>

            <div className="mt-5 rounded-2xl bg-cream-50 border border-ink-900/10 p-5">
              <p className="mono text-eyebrow uppercase tracking-widest text-ink-600 font-semibold mb-2">
                Prefer email
              </p>
              <p className="text-body-sm text-ink-700 leading-relaxed">
                Send your request to{' '}
                <a className="text-terra-500 hover:text-terra-600 underline underline-offset-2" href="mailto:privacy@pulsesignal.co">
                  privacy@pulsesignal.co
                </a>{' '}
                with the same information. We reply to whatever address you write from. For India DPDP grievances, copy{' '}
                <a className="text-terra-500 hover:text-terra-600 underline underline-offset-2" href="mailto:privacy@pulsesignal.co">
                  privacy@pulsesignal.co
                </a>
                .
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
          {state.kind === 'success' ? (
            <div className="bg-cream-50 border border-olive-600/30 rounded-2xl p-6">
              <h2 className="text-h-sm font-semibold tracking-tight text-ink-900 mb-2">
                Request received
              </h2>
              <p className="text-body-sm text-ink-800 leading-relaxed">
                Your reference ID is <code className="mono text-ink-900">{state.requestId}</code>.
                Expected response within: {state.expectedWithin}. Keep this ID for any
                follow-up correspondence.
              </p>
              <p className="text-body-sm text-ink-800 leading-relaxed mt-4">
                If you need to add information, reply to the confirmation email or write to{' '}
                <a className="text-terra-500 hover:text-terra-600" href="mailto:privacy@pulsesignal.co">
                  privacy@pulsesignal.co
                </a>
                .
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="bg-cream-50 border border-ink-900/10 rounded-2xl p-6 sm:p-8 space-y-6"
            >
              <Field
                label="Your full legal name"
                hint="Use the name as it appears on the record you want removed."
              >
                <input
                  required
                  minLength={2}
                  maxLength={256}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  aria-label="Your full legal name"
                  className="cream-input"
                  placeholder="Jane Smith"
                />
              </Field>

              <Field label="Email for correspondence">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Email for correspondence"
                  className="cream-input"
                  placeholder="you@example.com"
                />
              </Field>

              <Field
                label="Companies House numbers (optional)"
                hint="Comma- or space-separated. Helps us locate records faster."
              >
                <input
                  value={companyRefsRaw}
                  onChange={(e) => setCompanyRefsRaw(e.target.value)}
                  aria-label="Companies House numbers (optional)"
                  className="cream-input"
                  placeholder="01234567, 09876543"
                />
              </Field>

              <Field label="Additional context (optional)">
                <textarea
                  rows={5}
                  maxLength={4000}
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  aria-label="Additional context (optional)"
                  className="cream-input resize-y"
                  placeholder="I was a director at Acme Ltd from 2010 to 2015. Please remove my record from your mirror of the Companies House officer data."
                />
              </Field>

              {state.kind === 'error' && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="rounded-xl border border-terra-500/30 bg-terra-500/10 px-4 py-3 text-body-sm text-ink-800"
                >
                  {state.message}
                </div>
              )}

              <button
                type="submit"
                disabled={state.kind === 'submitting'}
                className="btn-primary disabled:opacity-50"
              >
                {state.kind === 'submitting' ? 'Submitting…' : 'Submit erasure request'}
              </button>
            </form>
          )}
          </div>
        </div>
      </article>

      <MarketingFooter />

      <style>{`
        .cream-input {
          width: 100%;
          background: #fbf7ec;
          border: 1px solid rgba(11, 12, 16, 0.12);
          border-radius: 10px;
          padding: 10px 14px;
          color: #0b0c10;
          /* 16px floor prevents iOS Safari auto-zoom on focus. */
          font-size: 16px;
          transition: border-color .15s, box-shadow .15s;
        }
        .cream-input::placeholder { color: rgba(11,12,16,0.40); }
        .cream-input:focus {
          outline: none;
          border-color: rgba(199, 93, 44, 0.55);
          box-shadow: 0 0 0 3px rgba(199, 93, 44, 0.15);
        }
      `}</style>
    </main>
  )
}

function Field({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mono text-eyebrow uppercase tracking-widest text-ink-600 font-semibold block mb-2">
        {label}
      </span>
      {children}
      {hint && <p className="mono text-caption text-ink-600 mt-1.5">{hint}</p>}
    </label>
  )
}
